import { useMemo } from 'react'
import { getIBPMenu, isAccountClosed } from 'helpers'
import { userAccountInfo } from 'helpers/user'
import { MainMenuProps, SubMenuProps } from 'typings/menu'
import getMenuItems from 'ui/organisms/Menu/menuItems'
import { clientInfo } from 'configureBrand'
import { useShallowEqualSelector } from 'redux/hooks'

/**
 * This hook returns array of menu props objects with menu items conditioned on
 * relevant values from the Redux store.
 */
// eslint-disable-next-line max-lines-per-function
export const useUpdatedMenuList = (): Array<MainMenuProps> => {
  const { clientName } = clientInfo
  const { isProx, accountType } = userAccountInfo

  const {
    statementEnrollment,
    conditions,
    isParentAccount,
    enrollEstatementFlag,
    relationship,
    ibpFlag
  } = useShallowEqualSelector(({ accountSummary, cardInfo, appInfo }) => {
    const { ibpDetails } = cardInfo.childAccounts?.[0] || {}

    return {
      statementEnrollment: accountSummary.response.billing.statementEnrollment,
      conditions: accountSummary.response.conditions,
      isParentAccount: appInfo.isParentAccount,
      enrollEstatementFlag: cardInfo.ebpDetails.flag ?? 'P',
      relationship: cardInfo.relationship ?? '',
      ibpFlag: ibpDetails?.flag ?? ''
    }
  })

  const parentMenuItem = [
    'manageUser',
    'editBusinessInfo',
    'editUserInformation',
    'manageLinkedAccounts'
  ]

  const paperStatementList = [null, '', 'P', 'Z']

  const menuItemsList = useMemo(
    (): Array<MainMenuProps> => getMenuItems(clientName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountType]
  )

  return useMemo(() => {
    let deleteItem: Array<string> = []
    if (isProx) {
      deleteItem = paperStatementList.includes(
        enrollEstatementFlag?.toUpperCase()
      )
        ? ['viewStatement', 'statements']
        : ['enrollEmailStatement']
    } else {
      deleteItem =
        statementEnrollment === 'PAPER'
          ? ['viewStatement']
          : ['enrollEmailStatement']
    }
    const menuItems = getIBPMenu(ibpFlag)
    deleteItem = [...deleteItem, ...menuItems]
    if (relationship?.toUpperCase() !== 'PARENT') {
      deleteItem = [...deleteItem, 'manageChildAccount']
    }
    if (!isParentAccount) {
      deleteItem = [...deleteItem, ...parentMenuItem]
    }
    if (conditions.some(isAccountClosed)) {
      deleteItem = [
        ...deleteItem,
        'closeAccount',
        'creditLineIncrease',
        'requestBuyerCards',
        'lostCard'
      ]
    }
    return menuItemsList.map((item: MainMenuProps) => {
      const subMenuItems = (item.subMenuItems || []).filter(
        (sub: SubMenuProps) => {
          return deleteItem.indexOf(sub.menuId) < 0
        }
      )
      return { ...item, subMenuItems }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollEstatementFlag,
    ibpFlag,
    isParentAccount,
    menuItemsList,
    relationship,
    statementEnrollment,
    conditions
  ])
}
